/* eslint-disable no-param-reassign */
import React from 'react';
import { Box } from '@mui/material';
import { Updater } from 'use-immer';
import { FIELDS_ENUM, isValidDate } from 'shared/utilities';
import ToggleableField from './ToggleableField';

interface Props {
  source: any;
  fields: Array<ToggleableField>;
  hasAttemptedSave?: boolean;
  isEditing?: boolean;
  labelGetter: (fieldName: string) => string;
  renderAsList?: boolean;
  setSource?: Updater<any>;
  narrow?: boolean;
  singleColumn?: boolean;
  type?: ProfileType;
  useCappedLabels?: boolean;
  useIcon?: boolean;
  isIndividualSponsored?: boolean;
  route?: string
}

const ToggleableForm: React.FC<Props> = ({
  source,
  fields,
  hasAttemptedSave,
  isEditing,
  labelGetter,
  renderAsList,
  setSource,
  singleColumn,
  narrow,
  type,
  useCappedLabels,
  useIcon,
  isIndividualSponsored,
  route,
}: any) => {
  const isWide = !narrow && !singleColumn;
  const gap = source?.companyDataId ? { gap: '1rem' } : { columnGap: '1rem' };
  let fieldData: ToggleableField[] = [];

  if (source) {
    fieldData = fields.map((field: ToggleableField) => {
      if (field.subFieldName) {
        field.value = source[field.subFieldName]
          ? source[field.subFieldName][field.fieldName]
          : null;
      } else if (field.fieldName) {
        field.value = source[field.fieldName];
      }

      if (field.type === 'checkbox') {
        field.value = field.options
          ?.map((option) => (source[option.fieldName] ? option.label : null))
          ?.filter((option) => !!option)
          ?.join(', ');
      }
      if ((field.subFieldName === 'employeeData' || field.subFieldName === 'foreignNational') && field.fieldName === 'employeeId') {
        // eslint-disable-next-line max-len
        if (!source[field.subFieldName] || source[field.subFieldName]?.firstname === '' || isIndividualSponsored) {
          return <></>;
        }
      }

      return field;
    });
  }

  return source && (
    <Box
      sx={(t) => ({
        alignItems: 'flex-start',
        display: 'grid',
        gridTemplateColumns: '100%',
        gridTemplateRows: 'auto',
        ...gap,
        [t.breakpoints.up('lg')]: {
          gridTemplateColumns: isWide ? '1fr 1fr' : '1fr',
        },
      })}
    >
      {fields.map((field: ToggleableField) => {
        let value: any = null;
        if (field.fieldName === FIELDS_ENUM.VISA_EXPIRATION) {
          const visaExpiration = source[field.fieldName] as string;
          value = isValidDate(visaExpiration) ? visaExpiration : null;
        } else if (field.subFieldName) {
          value = source[field.subFieldName]
            ? source[field.subFieldName][field.fieldName]
            : null;
        } else if (field.fieldName) {
          value = source[field.fieldName];
        }

        if (field.type === 'checkbox') {
          value = field.options
            ?.map((option) => (source[option.fieldName] ? option.label : null))
            ?.filter((option) => !!option)
            ?.join(', ');
        }
        if ((field.subFieldName === 'employeeData' || field.subFieldName === 'foreignNational') && field.fieldName === 'employeeId') {
          // eslint-disable-next-line max-len
          if (!source[field.subFieldName] || source[field.subFieldName]?.firstname === '' || isIndividualSponsored) {
            return null;
          }
        }

        const travelIsRequired : any = fieldData?.filter(({ fieldName }: any) => fieldName === 'travelIsRequired');
        const supervisesOthers : any = fieldData?.filter(({ fieldName }: any) => fieldName === 'supervisesOthers');
        const hasSSN : any = fieldData?.filter(({ fieldName }: any) => fieldName === 'hasSSN');

        if (!travelIsRequired[0]?.value && (field.fieldName === 'travelPercentage' || field.fieldName === 'travelType')) {
          return null;
        }

        if (!supervisesOthers[0]?.value && (field.fieldName === 'supervisesOthersCount' || field.fieldName === 'supervisesTitles')) {
          return null;
        }

        if (!hasSSN[0]?.value && field.fieldName === 'usSSN') {
          return null;
        }

        return (
          <ToggleableField
            key={field.fieldName}
            field={field}
            hasAttemptedSave={hasAttemptedSave}
            isEditing={isEditing}
            labelGetter={labelGetter}
            renderAsList={renderAsList}
            edits={source}
            setEdits={setSource}
            type={type}
            useCappedLabels={useCappedLabels}
            useIcon={useIcon}
            value={value}
            route={route}
            fieldData={fieldData}
          />
        );
      })}
    </Box>
  );
};

export default ToggleableForm;
